import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Force refresh and clear all caches
  const forceRefresh = useCallback(async () => {
    console.log('[Auth] Session invalid - forcing refresh...');
    
    // Clear Supabase session from localStorage
    await supabase.auth.signOut();
    
    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    
    // Hard reload with cache bust
    window.location.href = `${window.location.origin}?bust=${Date.now()}`;
  }, []);

  // Check session validity periodically
  useEffect(() => {
    const isRemembered = localStorage.getItem("remember-me") === "true";
    let consecutiveFailures = 0;
    const MAX_FAILURES = isRemembered ? 5 : 2;
    const CHECK_INTERVAL = isRemembered ? 120000 : 60000; // 2min vs 1min
    let lastVisibleTime = Date.now();
    let pausedSinceHidden = false;

    const checkSession = async () => {
      if (!session) return;
      
      // Skip check if we just came back from background (give network time to reconnect)
      if (pausedSinceHidden) {
        pausedSinceHidden = false;
        console.log('[Auth] Skipping session check — just resumed from background');
        return;
      }

      try {
        const { data, error } = await supabase.auth.getUser();
        
        if (error || !data.user) {
          consecutiveFailures++;
          console.log(`[Auth] Session check failed (${consecutiveFailures}/${MAX_FAILURES}):`, error?.message);
          if (consecutiveFailures >= MAX_FAILURES) {
            await forceRefresh();
          }
        } else {
          consecutiveFailures = 0;
        }
      } catch (err) {
        consecutiveFailures++;
        console.error(`[Auth] Session check error (${consecutiveFailures}/${MAX_FAILURES}):`, err);
        if (consecutiveFailures >= MAX_FAILURES) {
          await forceRefresh();
        }
      }
    };

    // Handle visibility changes (mobile tab suspend/resume)
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastVisibleTime = Date.now();
      } else {
        const hiddenDuration = Date.now() - lastVisibleTime;
        // If hidden for more than 5 seconds, skip the next check
        if (hiddenDuration > 5000) {
          pausedSinceHidden = true;
          console.log(`[Auth] Resumed after ${Math.round(hiddenDuration / 1000)}s in background — will skip next check`);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    // Don't check immediately on mount — give the app time to stabilize
    const initialDelay = setTimeout(() => {
      if (session) checkSession();
    }, 10000);

    const interval = setInterval(checkSession, CHECK_INTERVAL);
    
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [session, forceRefresh]);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[Auth] State change:', event);
        
        // Handle session errors - force refresh
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.log('[Auth] Token refresh failed - forcing logout');
          await forceRefresh();
          return;
        }
        
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.log('[Auth] Initial session error (non-fatal):', error.message);
        // Don't force refresh on transient errors — just start with no session
        // The auth state listener will pick up valid sessions when network recovers
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [forceRefresh]);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Signed out",
        description: "You've been successfully signed out.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};